<script setup>
import { onMounted, ref, computed } from "vue";
import { v4 as uuid } from "uuid";
import { useAttachments } from "@/composables/useAttachments";
import { useMessengerStore } from "@/stores/messenger";
import { useOrganizationStore } from "@/stores/organization";
import AttachmentCoursel from "./AttachmentCoursel.vue";

const props = defineProps({
  sendMessage: {
    type: Function,
    required: true,
  },
  stopResponse: {
    type: Function,
    required: false,
  },
  showStopButton: {
    type: Boolean,
    default: false,
  },
  isConnected: {
    type: Boolean,
    default: false,
  }
});

const messengerStore = useMessengerStore();
const organizationStore = useOrganizationStore();
const { attachments, handleFileChange, handleFileDelete } = useAttachments();

const textarea = ref(null);
const message = ref("");
const currentMessageId = ref(null);
const fileInput = ref(null);

onMounted(() => {
  autoResizeTextarea();
});

// need a way of trigging the input click, when the attachment button is clicked
const triggerFileSelect = () => {
  fileInput.value.click();
};

const autoResizeTextarea = () => {
  const textareaElement = textarea.value;
  textareaElement.style.height = "auto"; // Reset height before resizing
  textareaElement.style.height = `${textareaElement.scrollHeight}px`; // Resize based on scrollHeight
};

const handleSendMessage = () => {
  if (message.value) {
    const requestId = uuid();

    props.sendMessage(message.value, requestId, attachments.value);

    currentMessageId.value = requestId;

    // clearing the ui, idk why ref is not updating this
    message.value = "";
    textarea.value.value = "";
    attachments.value = [];
    autoResizeTextarea();
  }
};

const handleKeydown = (event) => {
  if (!messengerStore.isBotLoading) {
  }

  if (event.key === "Enter" && !event.shiftKey && !props.showStopButton) {
    event.preventDefault();
    handleSendMessage();
  }

  if (event.key === "Escape" && !event.shiftKey && props.showStopButton) {
    event.preventDefault();
    handleStopResponse();
  }
};

const handleStopResponse = async () => {
  const res = await props.stopResponse(currentMessageId.value);
  if (res?.ok) {
    console.log("cancel request successful")
  }
};

const placeholderText = computed(() => {
  return props.isConnected
    ? `Message ${organizationStore.botName}`
    : `Connecting to ${organizationStore.botName}, please wait...`;
});
</script>

<template>
  <div
    :class="{
      'grayed-out': !props.isConnected,
    }"
    class="message-composer-actions"
  >
    <div v-if="attachments.length" class="mb-3">
      <AttachmentCoursel
        :attachments="attachments"
        @delete-file="handleFileDelete"
      />
    </div>
    <textarea
      ref="textarea"
      @input="autoResizeTextarea"
      @keydown="handleKeydown"
      v-model="message"
      class="textarea-custom"
      :placeholder="placeholderText"
    >
    </textarea>
    <div class="composer-controls mt-2">
      <button @click="triggerFileSelect">
        <Icon icon="paperclip" style="margin-right: 0.5em" />Attach
        <input
          ref="fileInput"
          type="file"
          multiple
          @change="handleFileChange"
          style="display: none"
        />
      </button>
      <button
        :disabled="message === '' && !props.showStopButton"
        :class="{ 'grayed-out': message === '' && !props.showStopButton}"
        @click="props.showStopButton ? handleStopResponse() : handleSendMessage()"
      >
        <Icon v-if="props.showStopButton" icon="stop-circled" style="color: red" />
        <Icon v-else icon="arrow-right" style="color: black" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.message-composer-container {
  max-width: 1000px;
  width: 85%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
}

.message-composer-actions {
  border: 1px solid #e6e6e6;
  border-radius: 15px;
  box-shadow: 0 10px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  padding: 1em;
  margin-top: 1em;
}

.composer-controls {
  display: flex;
  justify-content: space-between;
}

.composer-controls button {
  border: 1px solid #e6e6e6;
  background: transparent;
  border-radius: 10px;
  padding: 0.5em 1em;
  /* color: #848484; */
  display: flex;
  align-items: center;
}

button:hover {
  background: var(--primary-250);
  color: white;
}

button:hover svg {
  fill: white;
}

.textarea-custom {
  display: block;
  width: 100%;
  height: auto;
  min-height: 1.5em;
  max-height: 200px;
  resize: none;
  border: 0;
  background-color: transparent;
  outline: none;
}

.textarea-custom::placeholder {
  padding-left: 1px;
}

.grayed-out {
  opacity: 0.5;
  pointer-events: none; /* Disable interactions */
}
</style>
